import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from './Button';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useFetchTerms from '@/features/agent/api/useFetchTerms';
import useAgreeTerms from '@/features/agent/api/useAgreeTerms';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import React from 'react';

interface TermsOfServiceDialogProps {
  open: boolean;
  onClose?: () => void;
}

export default function TermsOfServiceDialog({ open, onClose }: TermsOfServiceDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { data: terms, isLoading: isLoadingTerms } = useFetchTerms(open);
  const { mutateAsync: agreeTerms } = useAgreeTerms();

  const handleAgree = async () => {
    setIsLoading(true);
    try {
      await agreeTerms();
      // IAMキャッシュを更新して利用規約の同意状態を反映
      queryClient.invalidateQueries({ queryKey: ['iam'] });
      onClose?.();
    } catch (error) {
      showErrorToast('利用規約の同意に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsLinkClick = () => {
    if (terms?.pdf_url) {
      window.open(terms.pdf_url, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="w-full max-w-screen-sm rounded-sm pt-5 pb-4 shadow-xl!">
        <DialogTitle className="mb-8 border-b border-solid border-border px-6 pb-4 text-base! font-bold! text-gray-900">
          利用規約の同意が必要です
        </DialogTitle>

        {terms?.announcement && (
          <div className="mb-6 px-6">
            <p className="text-base leading-relaxed text-gray-700">
              {terms.announcement.split('\n').map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  {idx !== terms.announcement.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
        )}

        <div className="mb-4 flex items-center justify-center border-b border-solid border-border px-6 pb-6">
          <button
            onClick={handleTermsLinkClick}
            disabled={!terms?.pdf_url}
            className="hover:text-primary-hover flex items-center gap-2 text-primary underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span>利用規約</span>
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </button>
        </div>

        <div className="flex justify-end px-6">
          <Button
            onClick={handleAgree}
            disabled={isLoading || isLoadingTerms}
            color="primary"
            size="md"
            className="min-w-[120px]"
          >
            {isLoading ? '更新中' : '同意して続ける'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
